import React, { useEffect, useState } from "react";
import {
  Box,
  IconButton,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Typography,
  DialogActions,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNavigate } from "react-router-dom";
import Loader from "../../helpers/Loader";
import WarnModal from "../../components/modals/WarnModal";
import Pagination from "../../components/Pagination";
import SearchBar from "../../components/SearchBar";
import { isValidInput } from "../../utils/validation";
import { errorToast, successToast } from "../../helpers";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  useAddCategoryMutation,
  useDeleteByIdCategoryMutation,
  useLazyGetAllCategoryQuery,
  useUpdateCategoryMutation,
} from "../../services/category";
import { generateEncryptedKeyBody } from "../../utils/crypto";
import { CommonBody } from "../../types/General";
import moment from "moment";

export interface Category {
  category: string;
  status: boolean;
  createAt: string;
  _id: string;
}

const Pending = ({}: any) => {
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  let totalPages = Math.ceil(totalCount / 10);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useState(false);
  const [categoryInput, setCategoryInput] = useState("");
  const [inputError, setInputError] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const handleClose = () => {
    setOpenModal(false);
    setCategoryInput("");
    setEditingId(null);
  };
  const [rows, setRows] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [open, setOpen] = useState(false);

  const [getAllCat] = useLazyGetAllCategoryQuery();
  const [deleteCat] = useDeleteByIdCategoryMutation();
  const [addCat] = useAddCategoryMutation();
  const [updateCat] = useUpdateCategoryMutation();

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const response = await getAllCat({
        page: page,
        search: debouncedSearchTerm,
      }).unwrap();
      if (response.statusCode === 200) {
        setRows(response.data?.data);
        setTotalCount(response?.data?.total);
      }
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCat = async (id: any) => {
    try {
      const response = await deleteCat({ id }).unwrap();
      if (response?.statusCode === 200) {
        successToast("Category deleted successfully");
        fetchCategories();
      }
    } catch (error: any) {
      console.error(error);
      errorToast(error?.message || "");
    }
  };

  const changeStatus = async (id: any, status: any) => {
    try {
      const body = {
        status: status,
      };
      const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;

      const response = await updateCat({ body: encryptedBody, id }).unwrap();
      if (response?.statusCode === 200) {
        successToast("Category status updated successfully");
        fetchCategories();
      }
    } catch (error: any) {
      errorToast(error?.message);
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [page, debouncedSearchTerm]);

  const handleSaveCategory = async () => {
    if (categoryInput.trim().length === 0 || categoryInput.length > 100) {
      setInputError(true);
      return;
    }
    const body = {
      name: categoryInput,
    };

    if (editingId) {
      // Update existing category
      try {
        setIsLoading(true);
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response: any = await updateCat({
          body: encryptedBody,
          id: editingId,
        }).unwrap();
        if (response?.statusCode === 200) {
          successToast("Category updated successfully");
          fetchCategories();
          // successToast("Forum updated successfully");
          // navigate("/manage-forum");
        }
      } catch (error: any) {
        console.error("Error update:", error);
        errorToast(error?.data?.message || "");
      } finally {
        setIsLoading(false);
      }
    } else {
      // Add new category

      try {
        setIsLoading(true);
        const encryptedBody = generateEncryptedKeyBody(body) as CommonBody;
        const response: any = await addCat({
          body: encryptedBody,
        }).unwrap();
        if (response?.statusCode === 200) {
          successToast("Category added successfully");
          fetchCategories();
          // successToast("Forum updated successfully");
          // navigate("/manage-forum");
        }
      } catch (error: any) {
        console.error("Error adding:", error);
        errorToast(error?.data?.message || "");
      } finally {
        setIsLoading(false);
      }
    }

    handleClose();
  };

  const handleEditClick = (id: string) => {
    const categoryToEdit = rows.find((row) => row._id === id);
    if (categoryToEdit) {
      setCategoryInput(categoryToEdit.name);
      setEditingId(id);
      setOpenModal(true);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [page, debouncedSearchTerm]);

  return (
    <div>
      <Loader isLoad={isLoading} />
      <Box className="cards_header">
        <SearchBar
          value={searchTerm}
          searchTerm={searchTerm}
          onCross={() => setSearchTerm("")}
          setDebouncedSearchTerm={setDebouncedSearchTerm}
          onChange={(val: any) => {
            if (isValidInput(val.target.value)) {
              setSearchTerm(val.target.value);
            }
          }}
        />

        <Box className="cards_header_right">
          <Button
            className="btn btn_primary"
            onClick={() => setOpenModal(true)}
          >
            <AddIcon />
            Add Category
          </Button>
        </Box>
      </Box>
      <TableContainer className="table_container">
        <Box className="heading"></Box>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell align="center">S.No</TableCell>
              <TableCell align="center">Category Name</TableCell>
              <TableCell align="center">Created At</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {rows?.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  No Categories found
                </TableCell>
              </TableRow>
            ) : (
              rows?.map((row: any, i: number) => (
                <TableRow key={row._id}>
                  <TableCell align="center">
                    {(page - 1) * 10 + i + 1}
                  </TableCell>
                  <TableCell>{row?.name}</TableCell>
                  <TableCell>{moment(row.createdAt).format(" lll ")}</TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={row.status}
                      onChange={() => changeStatus(row._id, !row?.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Box className="table_actions">
                      <IconButton
                        onClick={() =>
                          navigate(
                            `/manage-subject-category/details/${row._id}`
                          )
                        }
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton onClick={() => handleEditClick(row._id)}>
                        <ModeEditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => {
                          setOpen(true);
                          setSelectedId(row._id);
                        }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {rows?.length > 0 ? (
        <Pagination
          setPage={setPage}
          module={rows}
          page={page}
          onPageChange={onPageChange}
          totalPages={totalPages}
        />
      ) : (
        ""
      )}
      <Dialog open={openModal} onClose={handleClose} className="revert_dialog">
        <DialogTitle>
          {editingId ? "Edit Category" : "Add Category"}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="category-input"
            label="Category Name"
            type="text"
            fullWidth
            value={categoryInput}
            onChange={(e) => {
              const inputValue = e.target.value;
              if (inputValue.length <= 100) {
                setCategoryInput(inputValue);
                setInputError(false);
              } else {
                setInputError(true);
              }
            }}
            error={inputError}
            helperText={
              inputError
                ? "Category name can't be more than 100 characters."
                : ""
            }
          />
        </DialogContent>

        <DialogActions>
          <Button sx={{ background: "#65a442" }} onClick={handleClose}>
            Cancel
          </Button>
          <Button
            sx={{ background: "#65a442" }}
            onClick={handleSaveCategory}
            disabled={inputError || categoryInput.trim().length === 0}
          >
            {editingId ? "Update" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
      <WarnModal
        open={open}
        setOpen={setOpen}
        handleDelete={() => {
          handleDeleteCat(selectedId);
        }}
        name="Category"
      />
    </div>
  );
};

export default Pending;
